import React, { useRef, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Box,
  Badge,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { MIconButton } from "src/theme";
import PopoverMenu from "src/components/PopoverMenu";
import {
  fetchBookingAlerts,
  markBookingsSeen,
  confirmBooking,
  cancelBookingExplicit,
} from "src/redux/slices/bookings";
import { PATH_APP } from "src/routes/paths";

// Centro de alertas de RESERVAS — icono propio del TopBar, separado de la
// campana de pedidos. Muestra las reservas que requieren atención (no-vistas,
// pendientes de confirmar, próximas) con info rica (cliente, hora, comensales,
// estado) y permite confirmar in-situ. El "visto" persiste en BD compartido
// entre dispositivos (Booking.SeenByRestaurant).

const STATUS = {
  0: { label: "PENDIENTE", bg: "#fef3c7", color: "#92400e" },
  1: { label: "CONFIRMADA", bg: "#dcfce7", color: "#166534" },
  2: { label: "SENTADOS", bg: "#dbeafe", color: "#1d4ed8" },
  3: { label: "AUSENTE", bg: "#fee2e2", color: "#991b1b" },
  4: { label: "CANCELADA", bg: "#fee2e2", color: "#991b1b" },
  5: { label: "FINALIZADA", bg: "#f1f5f9", color: "#475569" },
};

// Hook compartido — encapsula fetch + polling + cálculos del badge. Lo usan
// tanto el wrapper standalone como el AlertsCenter unificado, para que el
// contador no se duplique y el polling no se monte dos veces.
export function useBookingAlertsData() {
  const dispatch = useDispatch();
  const { alerts } = useSelector((state) => state.bookings);
  const { user } = useSelector((state) => state.authJwt);

  useEffect(() => {
    if (!user?.id) return;
    // Un solo fetch al montar. BookingHub (SignalR) emite created/changed/
    // deleted; el reducer aplica los deltas sin necesidad de re-fetchear.
    // Polling eliminado (era 15 llamadas por sesión de 4 min).
    dispatch(fetchBookingAlerts());
  }, [dispatch, user?.id]);

  const unseen = useMemo(
    () => (alerts || []).filter((a) => !a.seenByRestaurant),
    [alerts],
  );
  const pendingCount = useMemo(
    () => (alerts || []).filter((a) => a.status === 0).length,
    [alerts],
  );
  const badge = pendingCount > 0 ? pendingCount : unseen.length;
  const hasPending = pendingCount > 0;
  return { alerts: alerts || [], unseen, pendingCount, badge, hasPending };
}

export default function BookingAlerts() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const { alerts, unseen, pendingCount, badge } = useBookingAlertsData();

  // El centro de alertas muestra SOLO lo accionable: reservas no vistas o
  // pendientes de confirmar (status 0). Las pendientes se quedan siempre
  // aunque ya estén "vistas" — el restaurador necesita actuar sobre ellas
  // (Confirmar/Rechazar); ocultarlas tras la primera mirada haría que se
  // olviden. Las ya vistas y no-pendientes desaparecen del panel; siguen
  // accesibles desde la vista /reservas.
  const sorted = useMemo(
    () =>
      (alerts || [])
        .filter((a) => !a.seenByRestaurant || a.status === 0)
        .sort((a, b) => {
          // Pendientes primero (acción requerida), después no-vistas por
          // creación desc.
          const aPending = a.status === 0;
          const bPending = b.status === 0;
          if (aPending !== bPending) return aPending ? -1 : 1;
          if (!!a.seenByRestaurant !== !!b.seenByRestaurant)
            return a.seenByRestaurant ? 1 : -1;
          return new Date(b.creationDate) - new Date(a.creationDate);
        }),
    [alerts],
  );

  const handleOpen = () => {
    setOpen(true);
    dispatch(fetchBookingAlerts());
  };

  const handleMarkAllSeen = () => {
    if (unseen.length === 0) return;
    dispatch(markBookingsSeen(unseen.map((a) => a.id)));
  };

  const handleConfirm = async (b) => {
    setBusyId(b.id);
    try {
      await dispatch(confirmBooking(b.id));
      // Confirmar implica verla.
      dispatch(markBookingsSeen([b.id]));
      enqueueSnackbar("Reserva confirmada", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e?.message || "No se pudo confirmar", { variant: "error" });
    } finally {
      setBusyId(null);
    }
  };

  // Cancelación con "deshacer" — el backend recibe la llamada SOLO si
  // pasan 5s sin pulsar DESHACER. Estrategia anti-clic-accidental: el
  // botón Cancelar pierde el "punto sin retorno" porque el camarero
  // podría darle por error en el panel rápido. Si pulsan DESHACER, el
  // timeout se cancela y no hay POST al backend (no se notifica al
  // cliente). El email solo se manda cuando la BD realmente se cambia.
  const handleCancel = (b) => {
    setBusyId(b.id);
    const timer = setTimeout(async () => {
      try {
        await dispatch(cancelBookingExplicit(b.id));
        dispatch(markBookingsSeen([b.id]));
        enqueueSnackbar(`Reserva de ${b.name} cancelada`, { variant: "success" });
      } catch (e) {
        enqueueSnackbar(e?.message || "No se pudo cancelar", { variant: "error" });
      } finally {
        setBusyId(null);
      }
    }, 5000);

    enqueueSnackbar(`Cancelando reserva de ${b.name}…`, {
      variant: "warning",
      autoHideDuration: 5000,
      action: (key) => (
        <Button
          color="inherit"
          size="small"
          sx={{ fontWeight: 800 }}
          onClick={() => {
            clearTimeout(timer);
            setBusyId(null);
            closeSnackbar(key);
            enqueueSnackbar("Cancelación deshecha", { variant: "info" });
          }}
        >
          DESHACER
        </Button>
      ),
    });
  };

  const handleView = (b) => {
    dispatch(markBookingsSeen([b.id]));
    setOpen(false);
    history.push(PATH_APP.tuCarta.reservas);
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          ...(open && { bgcolor: "action.selected" }),
        }}
        aria-label="Reservas"
      >
        <Badge badgeContent={badge} color="warning" max={99}>
          <EventAvailableIcon sx={{ color: badge > 0 ? "#eab308" : "text.secondary" }} />
        </Badge>
      </MIconButton>

      <PopoverMenu
        width={400}
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
      >
        <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Reservas
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {pendingCount > 0
                ? `${pendingCount} pendiente${pendingCount === 1 ? "" : "s"} de confirmar`
                : unseen.length > 0
                  ? `${unseen.length} sin revisar`
                  : "Todo al día"}
            </Typography>
          </Box>
          {unseen.length > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
              onClick={handleMarkAllSeen}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Marcar vistas
            </Button>
          )}
        </Box>

        <Divider />

        <Box sx={{ maxHeight: { xs: 360, sm: 420 }, overflowY: "auto" }}>
          {sorted.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <RestaurantMenuIcon sx={{ fontSize: 40, color: "text.disabled" }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No hay reservas que revisar.
              </Typography>
            </Box>
          ) : (
            sorted.map((b) => (
              <BookingAlertCard
                key={b.id}
                booking={b}
                busy={busyId === b.id}
                onConfirm={() => handleConfirm(b)}
                onCancel={() => handleCancel(b)}
                onView={() => handleView(b)}
              />
            ))
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            disableRipple
            onClick={() => {
              setOpen(false);
              history.push(PATH_APP.tuCarta.reservas);
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Ver todas las reservas
          </Button>
        </Box>
      </PopoverMenu>
    </>
  );
}

// Panel reutilizable — el cuerpo del popover de reservas sin la campana ni
// el contenedor (Popover/PopoverMenu). Lo usan tanto el wrapper standalone
// (BookingAlerts default export) como AlertsCenter cuando renderiza la tab
// "Reservas".
export function BookingAlertsPanel({ onClose }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [busyId, setBusyId] = useState(null);
  const { alerts, unseen, pendingCount } = useBookingAlertsData();

  const sorted = useMemo(
    () =>
      (alerts || [])
        .filter((a) => !a.seenByRestaurant || a.status === 0)
        .sort((a, b) => {
          const aPending = a.status === 0;
          const bPending = b.status === 0;
          if (aPending !== bPending) return aPending ? -1 : 1;
          if (!!a.seenByRestaurant !== !!b.seenByRestaurant)
            return a.seenByRestaurant ? 1 : -1;
          return new Date(b.creationDate) - new Date(a.creationDate);
        }),
    [alerts],
  );

  const handleMarkAllSeen = () => {
    if (unseen.length === 0) return;
    dispatch(markBookingsSeen(unseen.map((a) => a.id)));
  };

  const handleConfirm = async (b) => {
    setBusyId(b.id);
    try {
      await dispatch(confirmBooking(b.id));
      dispatch(markBookingsSeen([b.id]));
      enqueueSnackbar("Reserva confirmada", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e?.message || "No se pudo confirmar", { variant: "error" });
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = (b) => {
    setBusyId(b.id);
    const timer = setTimeout(async () => {
      try {
        await dispatch(cancelBookingExplicit(b.id));
        dispatch(markBookingsSeen([b.id]));
        enqueueSnackbar(`Reserva de ${b.name} cancelada`, { variant: "success" });
      } catch (e) {
        enqueueSnackbar(e?.message || "No se pudo cancelar", { variant: "error" });
      } finally {
        setBusyId(null);
      }
    }, 5000);
    enqueueSnackbar(`Cancelando reserva de ${b.name}…`, {
      variant: "warning",
      autoHideDuration: 5000,
      action: (key) => (
        <Button
          color="inherit"
          size="small"
          sx={{ fontWeight: 800 }}
          onClick={() => {
            clearTimeout(timer);
            setBusyId(null);
            closeSnackbar(key);
            enqueueSnackbar("Cancelación deshecha", { variant: "info" });
          }}
        >
          DESHACER
        </Button>
      ),
    });
  };

  const handleView = (b) => {
    dispatch(markBookingsSeen([b.id]));
    onClose?.();
    history.push(PATH_APP.tuCarta.reservas);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Reservas
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {pendingCount > 0
              ? `${pendingCount} pendiente${pendingCount === 1 ? "" : "s"} de confirmar`
              : unseen.length > 0
                ? `${unseen.length} sin revisar`
                : "Todo al día"}
          </Typography>
        </Box>
        {unseen.length > 0 && (
          <Button
            size="small"
            startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
            onClick={handleMarkAllSeen}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Marcar vistas
          </Button>
        )}
      </Box>
      <Divider />
      <Box sx={{ maxHeight: { xs: 360, sm: 420 }, overflowY: "auto" }}>
        {sorted.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <RestaurantMenuIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No hay reservas que revisar.
            </Typography>
          </Box>
        ) : (
          sorted.map((b) => (
            <BookingAlertCard
              key={b.id}
              booking={b}
              busy={busyId === b.id}
              onConfirm={() => handleConfirm(b)}
              onCancel={() => handleCancel(b)}
              onView={() => handleView(b)}
            />
          ))
        )}
      </Box>
      <Divider />
      <Box sx={{ p: 1 }}>
        <Button
          fullWidth
          disableRipple
          onClick={() => {
            onClose?.();
            history.push(PATH_APP.tuCarta.reservas);
          }}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Ver todas las reservas
        </Button>
      </Box>
    </Box>
  );
}

function BookingAlertCard({ booking, busy, onConfirm, onCancel, onView }) {
  const status = STATUS[booking.status] || STATUS[1];
  const isNew = !booking.seenByRestaurant;
  const isPending = booking.status === 0;
  // Cancelable: pendientes y confirmadas. Las ya sentadas/completadas
  // no se cancelan desde el panel rápido (caso raro que va por la vista
  // completa de reservas).
  const isCancelable = booking.status === 0 || booking.status === 1;

  const when = useMemo(() => {
    try {
      const d = new Date(booking.date);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString("es-ES", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (_) {
      return "—";
    }
  }, [booking.date]);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: isNew ? "rgba(250,204,21,0.06)" : "transparent",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        {isNew && (
          <FiberManualRecordIcon sx={{ fontSize: 10, color: "#eab308" }} />
        )}
        <Typography
          sx={{ fontWeight: 800, fontSize: 14, flex: 1, minWidth: 0 }}
          noWrap
          title={booking.name}
        >
          {booking.name || "Reserva"}
        </Typography>
        <Chip
          label={status.label}
          size="small"
          sx={{
            height: 18,
            fontSize: 9.5,
            fontWeight: 800,
            bgcolor: status.bg,
            color: status.color,
          }}
        />
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: isPending ? 1 : 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            {when}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PeopleAltIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            {booking.number || 0} pax
          </Typography>
        </Stack>
        {booking.phone && (
          <Typography variant="caption" color="text.secondary" noWrap>
            · {booking.phone}
          </Typography>
        )}
      </Stack>

      {(booking.allergies || "").trim() && (
        <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 700, display: "block" }}>
          Alergias: {booking.allergies}
        </Typography>
      )}

      <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
        {isPending && (
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            startIcon={
              busy ? <CircularProgress size={14} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />
            }
            onClick={onConfirm}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: "#22c55e",
              "&:hover": { bgcolor: "#16a34a" },
            }}
          >
            Confirmar
          </Button>
        )}
        {isCancelable && (
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
            onClick={onCancel}
            sx={{
              flex: isPending ? 1 : 1,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: "#ef4444",
              "&:hover": { bgcolor: "#dc2626" },
            }}
          >
            Cancelar
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={onView}
          sx={{
            flex: isPending || isCancelable ? "0 0 auto" : 1,
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          Ver
        </Button>
      </Stack>
    </Box>
  );
}
